// ===== World 2: Dishes – GRAVITY AND SLOPE =====
// Principle: the ball only rolls downhill; steep tray = fast, flat tray = slow; pick the slope that fits.
// Ball → goal: "Bobbel", a little soap ball → the washing-up bowl with foam.
import { defineWorld, defineObj } from '../../core/registry';
import { Physics } from '../../core/physics';
import { outline, bubble, selBox } from '../../core/draw-helpers';
import type { Ctx2D, Gfx, GameBody, ObjData, PartDef, ObjDef, Rect, Reskin, CorePartType, Vec, WorldDef, WorldMeta, SoundFn } from '../../core/types';
import { scene } from './scene';
import { levels } from './levels';
import { strings } from './strings';
import { K, DEG } from './shared';

// ---------- constants ----------
const INK = '#3B2A5C';
/** Outline colour (slightly lighter than the shared default) */
const EDGE = 'rgba(59,42,92,.85)';
/** Ladle: handle ending in an upturned spoon = jump ramp. Spoon centre, radius, arc (degrees), segments, thickness, handle x and length. */
const LADLE = { cx: 42, cy: -46, r: 46, a0: 90, a1: 40, n: 5, t: 14, hx: -52, hw: 188 };

interface TapData extends ObjData { dir?: number }
interface WaterData extends ObjData { w: number; h: number }
interface WaterState { wet: Set<GameBody>; splashT: number; sx?: number }

// ---------- drawing helpers ----------
/** Soap bubble: bluish rim whose width follows the radius (the shared bubble() with this world's colours) */
function soapBubble(ctx: Ctx2D, x: number, y: number, r: number, a?: number) {
  bubble(ctx, x, y, r, { fill: 'rgba(220,245,255,.35)', stroke: 'rgba(120,190,230,.9)', lineWidth: Math.max(1.5, r * 0.12), alpha: a ?? 0.8 });
}
function foam(ctx: Ctx2D, x: number, y: number, w: number, t: number) {
  // foam mountain: white circles in a row
  ctx.fillStyle = '#FFFFFF';
  const n = Math.max(3, Math.round(w / 22));
  for (let i = 0; i < n; i++) {
    const px = x - w / 2 + (i + .5) * w / n, r = 12 + ((i * 7) % 5) + Math.sin((t || 0) * 0.05 + i) * 1.2;
    ctx.beginPath(); ctx.arc(px, y - ((i * 5) % 3) * 3, r, 0, 7); ctx.fill();
  }
}

// Bobbel – the soap ball with a face.
// Local face, not the shared face(): the eyes sit lower (ey -0.08r) with bigger pupils (0.58), and the mouth is lower.
function soapBall(ctx: Ctx2D, x: number, y: number, r: number, roll: number | undefined, v: Vec, kind: string, sad: boolean | undefined, g: Gfx) {
  const sp = Math.hypot(v.x, v.y);
  ctx.save(); ctx.translate(x, y);
  g.shadowOn(ctx, 8, 5);
  const gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .1, 0, 0, r * 1.05);
  if (kind === 'hero') { gr.addColorStop(0, '#E6FBFF'); gr.addColorStop(0.5, '#6FD0F2'); gr.addColorStop(1, '#2F8FD0'); }
  else { gr.addColorStop(0, '#FFE6F0'); gr.addColorStop(0.5, '#FF9EC2'); gr.addColorStop(1, '#D9578C'); }
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
  // rainbow sheen
  ctx.beginPath(); ctx.arc(0, 0, r - 3, 0.15 * Math.PI, 0.55 * Math.PI); ctx.lineWidth = 2.5; ctx.strokeStyle = 'rgba(255,170,220,.8)'; ctx.stroke();
  ctx.save(); ctx.rotate(roll || 0);
  // small foam cap (rolls with the ball)
  ctx.fillStyle = '#fff';
  [[-r * .3, -r * .92, r * .32], [r * .12, -r * 1.02, r * .38], [r * .5, -r * .8, r * .26]].forEach(([px, py, pr]) => { ctx.beginPath(); ctx.arc(px, py, pr, 0, 7); ctx.fill(); });
  const lx = Math.max(-2.5, Math.min(2.5, v.x * 0.5)), ly = Math.max(-2.5, Math.min(2.5, v.y * 0.5));
  const scared = v.y > 9, fast = sp > 8.5;
  const ex = r * 0.34, ey = -r * 0.08, er = r * 0.27;
  for (const s of [-1, 1]) {
    ctx.beginPath(); ctx.arc(s * ex, ey, er, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
    ctx.beginPath(); ctx.arc(s * ex + lx * 0.6, ey + ly * 0.6, er * (scared ? 0.35 : 0.58), 0, 7); ctx.fillStyle = INK; ctx.fill();
  }
  ctx.fillStyle = INK; ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.lineCap = 'round';
  if (sad) { ctx.beginPath(); ctx.arc(0, r * 0.66, r * 0.26, 1.2 * Math.PI, 1.8 * Math.PI); ctx.stroke(); }
  else if (fast || scared) { ctx.beginPath(); ctx.ellipse(0, r * 0.45, r * 0.17, r * 0.22, 0, 0, 7); ctx.fill(); }
  else { ctx.beginPath(); ctx.arc(0, r * 0.28, r * 0.28, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke(); }
  ctx.fillStyle = 'rgba(255,120,170,.5)';
  [-1, 1].forEach(s => { ctx.beginPath(); ctx.ellipse(s * r * 0.6, r * 0.25, r * 0.14, r * 0.09, 0, 0, 7); ctx.fill(); });
  ctx.restore();
  ctx.beginPath(); ctx.ellipse(-r * .4, -r * .42, r * .26, r * .16, -0.6, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.85)'; ctx.fill();
  ctx.restore();
}

// Tray (orange plastic serving tray) seen from the side
function trayDraw(ctx: Ctx2D, x: number, y: number, a: number, w: number, h: number, sel: boolean, g: Gfx) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  g.shadowOn(ctx, 10, 5);
  // handles at the ends (below the rim, out of the ball's way)
  ctx.beginPath(); ctx.moveTo(-w / 2, -h / 2); ctx.lineTo(-w / 2 - 6, h / 2 + 4); ctx.lineTo(w / 2 + 6, h / 2 + 4); ctx.lineTo(w / 2, -h / 2); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -h / 2, h / 2 + 4, '#FFB27A', '#E8663A'); ctx.fill(); g.shadowOff(ctx);
  outline(ctx, 3, EDGE);
  g.rr(ctx, -w / 2 + 6, -h / 2 + 2, w - 12, 4, 2); ctx.fillStyle = 'rgba(255,255,255,.55)'; ctx.fill();
  // handle holes
  ctx.fillStyle = 'rgba(120,40,20,.55)';
  [-1, 1].forEach(s => { g.rr(ctx, s * (w / 2 - 22) - 9, 1, 18, 5, 2.5); ctx.fill(); });
  // small dots (pattern)
  ctx.fillStyle = 'rgba(255,255,255,.45)';
  for (let i = -2; i <= 2; i++) { ctx.beginPath(); ctx.arc(i * w / 8, 2, 2.2, 0, 7); ctx.fill(); }
  if (sel) selBox(ctx, -w / 2 - 10, -h / 2 - 8, w + 20, h + 20, 10);
  ctx.restore();
}
// Chopping board (reskinned plank)
function boardDraw(ctx: Ctx2D, x: number, y: number, a: number, w: number, h: number, sel: boolean, g: Gfx) {
  g.block(ctx, x, y, w, h, a, '#F6D29A', '#D8A15E', 7, { outline: sel ? '#FFC93C' : null });
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  // hanging hole
  ctx.beginPath(); ctx.arc(w / 2 - 14, 2, 4, 0, 7); ctx.fillStyle = 'rgba(90,50,20,.6)'; ctx.fill();
  // wood grain
  ctx.strokeStyle = 'rgba(160,90,30,.35)'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(-w / 2 + 10, 3); ctx.bezierCurveTo(-w / 6, -1, w / 8, 6, w / 2 - 26, 2); ctx.stroke();
  // small knife marks
  ctx.strokeStyle = 'rgba(120,70,30,.3)';
  for (const px of [-w / 4, -w / 8, 0]) { ctx.beginPath(); ctx.moveTo(px, -h / 2 + 3); ctx.lineTo(px + 6, -h / 2 + 6); ctx.stroke(); }
  ctx.restore();
}
// Sponge (yellow with a green scouring side)
function spongeDraw(ctx: Ctx2D, x: number, y: number, a: number, w: number, h: number, sel: boolean, g: Gfx, squish?: number) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  const s = squish || 0;
  g.shadowOn(ctx, 8, 4);
  g.rr(ctx, -w / 2, -h / 2 + 8, w, h - 8, 7); ctx.fillStyle = g.lg(ctx, -h / 2, h / 2, '#FFE66B', '#F2B92E'); ctx.fill(); g.shadowOff(ctx);
  g.rr(ctx, -w / 2, -h / 2 + 8, w, h - 8, 7); outline(ctx, 3, EDGE);
  g.rr(ctx, -w / 2, -h / 2 + s * 3, w, 11 - s * 3, 6); ctx.fillStyle = '#4CC36B'; ctx.fill(); outline(ctx, 2.5, EDGE);
  // holes in the sponge
  ctx.fillStyle = 'rgba(200,130,20,.45)';
  [[-30, 12, 3.5], [-12, 16, 2.5], [8, 11, 3], [26, 15, 3.5], [-22, 20, 2], [16, 20, 2]].forEach(([px, py, r]) => { if (Math.abs(px) < w / 2 - 6) { ctx.beginPath(); ctx.arc(px * w / 90, py - 13 + h / 2 * 0.5, r, 0, 7); ctx.fill(); } });
  // green scouring dots
  ctx.fillStyle = 'rgba(30,110,50,.55)';
  for (let i = -3; i <= 3; i++) { ctx.beginPath(); ctx.arc(i * w / 8, -h / 2 + 5, 1.6, 0, 7); ctx.fill(); }
  if (sel) selBox(ctx, -w / 2 - 8, -h / 2 - 8, w + 16, h + 16, 10);
  ctx.restore();
}
// Ladle bodies: the handle plus short segments along the spoon's arc
function ladleRects(): Rect[] {
  const S = LADLE, out: Rect[] = [{ x: S.hx, y: 0, w: S.hw, h: S.t }];
  const step = (S.a1 - S.a0) / S.n;
  for (let i = 0; i < S.n; i++) {
    const am = (S.a0 + step * (i + .5)) * DEG;
    out.push({ x: S.cx + Math.cos(am) * S.r, y: S.cy + Math.sin(am) * S.r, w: 2 * S.r * Math.sin(Math.abs(step) * DEG / 2) + 5, h: S.t, a: am / DEG + 90 });
  }
  return out;
}
function ladleDraw(ctx: Ctx2D, x: number, y: number, a: number, sel: boolean, g: Gfx) {
  const S = LADLE;
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  g.shadowOn(ctx, 10, 5);
  // handle
  const x0 = S.hx - S.hw / 2, x1 = S.cx;
  ctx.lineCap = 'round';
  ctx.strokeStyle = INK; ctx.lineWidth = S.t + 6;
  ctx.beginPath(); ctx.moveTo(x0, 0); ctx.lineTo(x1, 0); ctx.arc(S.cx, S.cy, S.r, S.a0 * DEG, S.a1 * DEG, true); ctx.stroke();
  g.shadowOff(ctx);
  ctx.strokeStyle = '#FF6B6B'; ctx.lineWidth = S.t;
  ctx.beginPath(); ctx.moveTo(x0, 0); ctx.lineTo(x1, 0); ctx.stroke();
  // the spoon (steel): filled bowl behind the rim
  ctx.beginPath(); ctx.arc(S.cx, S.cy, S.r + S.t / 2, (S.a0 + 30) * DEG, (S.a1 - 4) * DEG, true); ctx.closePath();
  ctx.fillStyle = 'rgba(200,212,230,.75)'; ctx.fill(); ctx.lineWidth = 2.5; ctx.strokeStyle = 'rgba(59,42,92,.6)'; ctx.stroke();
  ctx.strokeStyle = '#DCE3EE'; ctx.lineWidth = S.t;
  ctx.beginPath(); ctx.arc(S.cx, S.cy, S.r, (S.a0 + 8) * DEG, S.a1 * DEG, true); ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,.9)'; ctx.lineWidth = 3;
  ctx.beginPath(); ctx.arc(S.cx, S.cy, S.r - 4, (S.a0 - 2) * DEG, (S.a1 + 6) * DEG, true); ctx.stroke();
  // shine on the handle + hole for the hook
  ctx.strokeStyle = 'rgba(255,255,255,.55)'; ctx.lineWidth = 3;
  ctx.beginPath(); ctx.moveTo(x0 + 10, -3); ctx.lineTo(x1 - 16, -3); ctx.stroke();
  ctx.beginPath(); ctx.arc(x0 + 10, 1, 3.5, 0, 7); ctx.fillStyle = INK; ctx.fill();
  if (sel) selBox(ctx, x0 - 14, -S.cy * 0 - 64, x1 - x0 + S.r + 36, 86, 14, true);
  ctx.restore();
}
// Rubber glove (reskinned pad = spring)
function gloveDraw(ctx: Ctx2D, x: number, y: number, a: number, w: number, h: number, sel: boolean, g: Gfx, squash?: number) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  const s = squash || 0, top = -h / 2 + s * 3;
  g.shadowOn(ctx, 8, 4);
  // fingers (bumps) on top of an inflated palm
  ctx.beginPath();
  ctx.moveTo(-w / 2, h / 2 + 8);
  ctx.lineTo(-w / 2, top + 8);
  const n = 4, fw = (w - 26) / n;
  for (let i = 0; i < n; i++) { const fx = -w / 2 + i * fw; ctx.quadraticCurveTo(fx + fw / 2, top - 10 + s * 3, fx + fw, top + 6); }
  ctx.quadraticCurveTo(w / 2 - 6, top - 16, w / 2 + 6, top + 6);
  ctx.lineTo(w / 2 + 2, h / 2 + 8); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, top - 10, h / 2 + 8, '#FFE36B', '#F2B120'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3, EDGE);
  // cuff
  g.rr(ctx, -w / 2 - 6, h / 2 + 2, 24, 10, 4); ctx.fillStyle = '#FF8FA3'; ctx.fill(); outline(ctx, 2, EDGE);
  ctx.beginPath(); ctx.ellipse(-8, top + 6, 14, 3, 0, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.6)'; ctx.fill();
  if (sel) selBox(ctx, -w / 2 - 12, -h / 2 - 16, w + 24, h + 34, 10);
  ctx.restore();
}
// Tap
function tapDraw(ctx: Ctx2D, o: TapData, t: number, g: Gfx) {
  const d = o.dir || 1, x = o.x, y = o.y;
  ctx.save();
  // wall plate
  const wx = x - d * 110;
  g.shadowOn(ctx, 8, 4);
  g.rr(ctx, wx - 16, y - 116, 32, 44, 8); ctx.fillStyle = g.lg(ctx, y - 116, y - 72, '#F4F7FC', '#AEB8CC'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3, EDGE);
  // pipe: out of the wall, horizontal, bending down to the spout
  ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  const path = () => { ctx.beginPath(); ctx.moveTo(wx, y - 94); ctx.lineTo(x - d * 22, y - 94); ctx.quadraticCurveTo(x, y - 94, x, y - 70); ctx.lineTo(x, y - 30); };
  path(); ctx.strokeStyle = INK; ctx.lineWidth = 22; ctx.stroke();
  path(); ctx.strokeStyle = '#C9D2E2'; ctx.lineWidth = 16; ctx.stroke();
  path(); ctx.strokeStyle = 'rgba(255,255,255,.8)'; ctx.lineWidth = 4; ctx.stroke();
  // spout
  g.rr(ctx, x - 14, y - 36, 28, 12, 4); ctx.fillStyle = '#9AA6BA'; ctx.fill(); outline(ctx, 2.5, EDGE);
  // handle (red/blue knob)
  ctx.beginPath(); ctx.arc(wx + d * 40, y - 112, 10, 0, 7); ctx.fillStyle = '#FF6B6B'; ctx.fill(); outline(ctx, 2.5, EDGE);
  ctx.beginPath(); ctx.moveTo(wx + d * 40, y - 102); ctx.lineTo(wx + d * 40, y - 94); ctx.strokeStyle = INK; ctx.lineWidth = 4; ctx.stroke();
  // drip
  const k = (t % 90) / 90;
  if (k < 0.6) {
    const dy = y - 22 + (k < 0.3 ? k * 8 : 2.4 + (k - 0.3) * (k - 0.3) * 500);
    ctx.globalAlpha = k < 0.5 ? 1 : (0.6 - k) * 10;
    ctx.beginPath(); ctx.moveTo(x, dy - 7); ctx.quadraticCurveTo(x + 6, dy + 2, x, dy + 4); ctx.quadraticCurveTo(x - 6, dy + 2, x, dy - 7); ctx.fillStyle = '#7FD3FF'; ctx.fill();
    ctx.lineWidth = 1.5; ctx.strokeStyle = 'rgba(59,42,92,.5)'; ctx.stroke();
  }
  ctx.restore();
}

// ---------- skins (statics, ball, goal, background) ----------
const skins: WorldDef['skins'] = {
  // kitchen counter: white front with a wooden worktop
  dishes_table(ctx: Ctx2D, b: GameBody, g: Gfx) {
    const { x, y } = b.position;
    g.block(ctx, x, y, b.dw!, b.dh!, b.angle, '#FFFFFF', '#C9D4E6', 8);
    ctx.save(); ctx.translate(x, y); ctx.rotate(b.angle);
    g.rr(ctx, -b.dw! / 2, -b.dh! / 2, b.dw!, Math.min(12, b.dh! * 0.4), 6); ctx.fillStyle = '#E9C48C'; ctx.fill(); ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(59,42,92,.6)'; ctx.stroke();
    ctx.restore();
  },
  // shelf/drying rack in light blue
  dishes_rack: { c1: '#BFE6FA', c2: '#6FB3E0', r: 9 },
  // stack of plates (vertical wall)
  dishes_stack(ctx: Ctx2D, b: GameBody, g: Gfx) {
    const { x, y } = b.position, w = b.dw!, h = b.dh!;
    ctx.save(); ctx.translate(x, y); ctx.rotate(b.angle);
    g.shadowOn(ctx, 10, 5);
    g.rr(ctx, -w / 2, -h / 2, w, h, 8); ctx.fillStyle = '#F2F6FC'; ctx.fill(); g.shadowOff(ctx);
    ctx.save(); g.rr(ctx, -w / 2, -h / 2, w, h, 8); ctx.clip();
    for (let py = -h / 2 + 6; py < h / 2; py += 12) {
      g.rr(ctx, -w / 2 - 2, py - 5, w + 4, 10, 5); ctx.fillStyle = '#FFFFFF'; ctx.fill();
      ctx.lineWidth = 1.5; ctx.strokeStyle = 'rgba(90,140,200,.7)'; ctx.stroke();
      ctx.fillStyle = '#6FB3E0'; ctx.fillRect(-w / 2, py + 3, w, 2);
    }
    ctx.restore();
    g.rr(ctx, -w / 2, -h / 2, w, h, 8); outline(ctx, 3, EDGE);
    ctx.restore();
  },
  // steel sink
  dishes_sink: { c1: '#E8EDF5', c2: '#9AA6BA', r: 6 },
  // chopping board (fixed ramp)
  dishes_board(ctx: Ctx2D, b: GameBody, g: Gfx) { boardDraw(ctx, b.position.x, b.position.y, b.angle, b.dw!, b.dh!, false, g); },
  // tea towel (brakes): striped cloth
  dishes_cloth(ctx: Ctx2D, b: GameBody, g: Gfx) {
    const { x, y } = b.position, w = b.dw!, h = b.dh!;
    g.block(ctx, x, y, w, h, b.angle, '#FFFFFF', '#DDE6F2', 6);
    ctx.save(); ctx.translate(x, y); ctx.rotate(b.angle);
    g.rr(ctx, -w / 2 + 2, -h / 2 + 1, w - 4, h - 2, 5); ctx.clip();
    ctx.fillStyle = 'rgba(79,168,232,.8)';
    for (let px = -w / 2 + 10; px < w / 2; px += 28) ctx.fillRect(px, -h / 2, 8, h);
    ctx.fillStyle = 'rgba(255,107,107,.7)'; ctx.fillRect(-w / 2, -2, w, 4);
    ctx.restore();
  },
};

const ball: WorldDef['ball'] = (ctx, b, kind, t, g) => { soapBall(ctx, b.position.x, b.position.y, b.circleRadius!, b.roll, b.velocity || { x: 0, y: 0 }, kind, b.sad, g); };

const goal: WorldDef['goal'] = {
  // washing-up bowl: blue plastic tub with foam
  bowl(ctx: Ctx2D, gl: { x: number; y: number; w: number }, full: number, t: number, g: Gfx) {
    const { x, y, w } = gl;
    ctx.save();
    g.shadowOn(ctx, 14, 7);
    ctx.beginPath(); ctx.moveTo(x - w / 2 - 8, y - 56); ctx.lineTo(x - w / 2 + 6, y + 7); ctx.lineTo(x + w / 2 - 6, y + 7); ctx.lineTo(x + w / 2 + 8, y - 56); ctx.closePath();
    ctx.fillStyle = g.lg(ctx, y - 56, y + 7, '#7FD3FF', '#3E97D9'); ctx.fill(); g.shadowOff(ctx); outline(ctx, 3, EDGE);
    // ribs
    ctx.strokeStyle = 'rgba(255,255,255,.4)'; ctx.lineWidth = 4;
    ctx.beginPath(); ctx.moveTo(x - w / 2 + 2, y - 22); ctx.lineTo(x + w / 2 - 2, y - 22); ctx.stroke();
    // handles
    ctx.fillStyle = '#3E97D9';
    [-1, 1].forEach(s => { g.rr(ctx, x + s * (w / 2 + 4) - 9, y - 58, 18, 9, 4); ctx.fill(); outline(ctx, 2, EDGE); });
    // foam on top
    foam(ctx, x, y - 60, w + 4, t);
    if (full) for (let i = 0; i < 4; i++) { const k = ((t * 0.6 + i * 30) % 120) / 120; soapBubble(ctx, x - 40 + i * 26 + Math.sin(t * 0.05 + i) * 6, y - 70 - k * 70, 5 + i % 3 * 2, 1 - k); }
    else for (let i = 0; i < 2; i++) { const k = ((t * 0.4 + i * 60) % 120) / 120; soapBubble(ctx, x - 20 + i * 40, y - 72 - k * 40, 4 + i * 2, 0.8 - k * 0.8); }
    ctx.restore();
  },
  front(ctx: Ctx2D, gl: { x: number; y: number; w: number }) {
    const { x, y, w } = gl;
    ctx.save(); ctx.fillStyle = '#FFFFFF';
    for (let i = 0; i < 5; i++) { ctx.beginPath(); ctx.arc(x - w / 2 + 10 + i * (w - 20) / 4, y - 52, 9, 0, Math.PI); ctx.fill(); }
    ctx.restore();
  },
};

const bgExtra: WorldDef['bgExtra'] = (ctx) => {
  // a few big soap bubbles in the background (cheap, cached)
  const bs = [[70, 90, 16], [110, 60, 9], [1080, 120, 14], [1110, 90, 7], [640, 50, 10], [900, 60, 8], [330, 70, 7]];
  for (const [x, y, r] of bs) soapBubble(ctx, x, y, r, 0.55);
};

// ---------- parts ----------
const parts: Record<string, PartDef> = {
  // Tray: a ramp whose slope the player picks (10° per tap)
  dishes_tray: {
    rects: [{ x: 0, y: 0, w: 200, h: 16 }], rot: 10, minA: -60, maxA: 60, defaultA: 10, snap: true,
    friction: 0.03, restitution: 0.15,
    draw(ctx, pl, sel, g) { const r = Physics.worldRects(pl)[0]; trayDraw(ctx, r.x, r.y, r.a, r.w, r.h, sel, g); },
    icon(ctx, g) { trayDraw(ctx, 0, 0, 0.3, 96, 14, false, g); },
    intro(ctx, t, g, h) {
      // the tray tips: the ball always rolls towards the low end
      const side = t < 120 ? 1 : -1, tt = t % 120;
      const a = side * 20 * DEG;
      trayDraw(ctx, 230, 170, a, 220, 16, false, g);
      let bx = 230, by = 20 + 0.09 * tt * tt;
      const surf = (x: number) => 170 + Math.tan(a) * (x - 230) - 8 - 18;
      if (by >= surf(230)) { const k = tt - Math.sqrt((surf(230) - 20) / 0.09); const d = 0.05 * k * k; bx = 230 + side * d * Math.cos(a); by = surf(bx); if (Math.abs(bx - 230) > 120) { by += (Math.abs(bx - 230) - 120) * 2; } }
      g.ball(ctx, h.fakeBall(bx, by, side * 3, 2), 'hero', t);
      h.arrow(230, 20, 230, 40);
    },
  },
  // Sponge: brakes the ball
  dishes_sponge: {
    rects: [{ x: 0, y: 0, w: 90, h: 26 }], rot: 15, minA: -60, maxA: 60, defaultA: 0,
    drag: 0.86, friction: 0.03, restitution: 0.02,
    hit(api, body, other) { if (api.isBall(other)) api.sfx('dishes_squish', other.position.x, other.position.y, '#FFE66B', 4); },
    draw(ctx, pl, sel, g) { spongeDraw(ctx, pl.x, pl.y, (pl.a || 0) * DEG, 90, 26, sel, g); },
    icon(ctx, g) { spongeDraw(ctx, 0, 0, -0.2, 84, 30, false, g); },
    intro(ctx, t, g, h) {
      const a = 18 * DEG;
      g.block(ctx, 230, 170, 400, 14, a, '#BFE6FA', '#6FB3E0', 7);
      spongeDraw(ctx, 250, 170 + Math.tan(a) * 20 - 18, a, 90, 26, false, g);
      const k = t / 200;
      const s = k < 0.35 ? (k / 0.35) * 170 : 170 + (1 - Math.pow(1 - Math.min(1, (k - 0.35) / 0.65), 2)) * 60;
      const bx = 50 + s * Math.cos(a), by = 170 + Math.tan(a) * (bx - 230) - 7 - 20 - (s > 150 && s < 230 ? 8 : 0);
      g.ball(ctx, h.fakeBall(bx, by, k < 0.35 ? 8 : 1, 0), 'hero', t);
      ctx.strokeStyle = 'rgba(59,42,92,.35)'; ctx.lineWidth = 4; ctx.lineCap = 'round';
      const n = k < 0.35 ? 3 : 0;
      for (let i = 0; i < n; i++) { ctx.beginPath(); ctx.moveTo(bx - 28 - i * 10, by - 8 + i * 8); ctx.lineTo(bx - 50 - i * 14, by - 14 + i * 8); ctx.stroke(); }
    },
  },
  // Ladle: jump ramp – speed in, jump out
  dishes_ladle: {
    rects: ladleRects(), rot: 15, minA: -45, maxA: 45, defaultA: 15,
    friction: 0.01, restitution: 0.05,
    draw(ctx, pl, sel, g) { ladleDraw(ctx, pl.x, pl.y, (pl.a || 0) * DEG, sel, g); },
    icon(ctx, g) { ctx.scale(0.55, 0.55); ladleDraw(ctx, -10, 20, 0.2, false, g); },
    intro(ctx, t, g, h) {
      const a = 15 * DEG;
      ladleDraw(ctx, 200, 200, a, false, g);
      // the ball rolls down the handle and jumps out of the spoon
      const c = Math.cos(a), s = Math.sin(a);
      const P = (lx: number, ly: number) => ({ x: 200 + lx * c - ly * s, y: 200 + lx * s + ly * c });
      let p, vx = 3, vy = 2;
      if (t < 60) { const k = t / 60; p = P(-140 + k * k * 180, -22); }
      else if (t < 80) { const k = (t - 60) / 20, am = (90 - 62 * k) * DEG; p = P(LADLE.cx + Math.cos(am) * (LADLE.r - 22), LADLE.cy + Math.sin(am) * (LADLE.r - 22)); vy = -4; }
      else { const k = t - 80, e = P(LADLE.cx + Math.cos(28 * DEG) * (LADLE.r - 22), LADLE.cy + Math.sin(28 * DEG) * (LADLE.r - 22)); p = { x: e.x + k * 2.2, y: e.y - k * 5 + 0.05 * k * k }; vy = -5 + 0.1 * k; }
      if (p.y < 300) g.ball(ctx, h.fakeBall(p.x, p.y, vx, vy), 'hero', t);
      h.arrow(380, 60, 420, 40, true);
    },
  },
};

// ---------- objects ----------
const objs: Record<string, ObjDef> = {
  // Tap: the ball "drips" out of the tap
  dishes_tap: defineObj({
    layer: 'back',
    create() { return {}; },
    blockers(o) { return [{ x: o.x, y: o.y - 60, r: 34 }]; },
    draw(ctx, st, t, g) { tapDraw(ctx, st.o, t, g); },
  }),
  // Water in the sink: the ball splashes down and stays there (= fail)
  dishes_water: defineObj<WaterState>({
    layer: 'front',
    create() { return { wet: new Set(), splashT: -99 }; },
    step(api, st) {
      const o = st.o as WaterData;
      for (const b of api.S.dyn) {
        if (!api.isBall(b) || st.wet.has(b)) continue;
        if (Math.abs(b.position.x - o.x) < o.w / 2 && b.position.y > o.y - o.h / 2 + 4 && b.position.y < o.y + o.h / 2) {
          st.wet.add(b); st.splashT = api.S.t; st.sx = b.position.x;
          b.frictionAir = 0.12; b.sad = true;
          api.Body.setVelocity(b, { x: b.velocity.x * 0.2, y: b.velocity.y * 0.2 });
          api.sfx('dishes_splash', b.position.x, o.y - o.h / 2, '#7FD3FF', 14);
        }
      }
    },
    blockers(o) { const w = o as WaterData; return [{ x: w.x, y: w.y, r: Math.min(w.w, w.h) / 2 }]; },
    draw(ctx, st, t, g, S) {
      const o = st.o as WaterData, x0 = o.x - o.w / 2, y0 = o.y - o.h / 2;
      ctx.save();
      ctx.globalAlpha = 0.72;
      ctx.beginPath(); ctx.moveTo(x0, y0 + 4);
      for (let i = 0; i <= 12; i++) { const px = x0 + i * o.w / 12; ctx.lineTo(px, y0 + 4 + Math.sin(t * 0.06 + i * 1.3) * 2.5); }
      ctx.lineTo(x0 + o.w, y0 + o.h); ctx.lineTo(x0, y0 + o.h); ctx.closePath();
      ctx.fillStyle = g.lg(ctx, y0, y0 + o.h, '#9FE0FF', '#4FA8E8'); ctx.fill();
      ctx.globalAlpha = 1;
      // foam islands
      ctx.fillStyle = 'rgba(255,255,255,.95)';
      for (let i = 0; i < 3; i++) { const px = x0 + (i + .5) * o.w / 3 + Math.sin(t * 0.02 + i) * 6; [[-10, 0, 7], [0, -3, 9], [10, 0, 6]].forEach(([dx, dy, r]) => { ctx.beginPath(); ctx.arc(px + dx, y0 + 4 + dy, r, 0, 7); ctx.fill(); }); }
      // splash
      const k = S ? (S.t - st.splashT) : 99;
      if (k >= 0 && k < 30) { const sx = st.sx!; ctx.strokeStyle = '#7FD3FF'; ctx.lineWidth = 4; ctx.lineCap = 'round'; for (let i = -2; i <= 2; i++) { ctx.beginPath(); ctx.moveTo(sx + i * 8, y0); ctx.lineTo(sx + i * (10 + k), y0 - 30 * Math.sin(k / 30 * Math.PI) - 6); ctx.stroke(); } }
      ctx.restore();
    },
  }),
};

// ---------- sounds ----------
const sounds: Record<string, SoundFn> = {
  dishes_splash: (tone, noise) => { noise(0.35, 0.35, 900, 0.8); tone(420, 0.18, 'sine', 0.15, 160); },
  dishes_squish: (tone, noise) => { noise(0.18, 0.2, 500, 1.5); tone(180, 0.15, 'triangle', 0.12, 120); },
  dishes_bloop: (tone) => { tone(500, 0.12, 'sine', 0.2, 900); },
};

// ---------- reskins (the kit's plank and pad as chopping board and rubber glove) ----------
const reskin: Partial<Record<CorePartType, Reskin>> = {
  plank(ctx, pl, sel, g, rs) { const r = rs[0]; boardDraw(ctx, r.x, r.y, r.a, r.w, r.h, sel, g); },
  long_plank(ctx, pl, sel, g, rs) { const r = rs[0]; boardDraw(ctx, r.x, r.y, r.a, r.w, r.h, sel, g); },
  pad(ctx, pl, sel, g, rs) { const r = rs[0]; gloveDraw(ctx, r.x, r.y, r.a, r.w, r.h, sel, g, pl.squash); },
};
const icons: WorldDef['icons'] = {
  plank(ctx, g) { boardDraw(ctx, 0, 0, -0.35, 100, 18, false, g); },
  long_plank(ctx, g) { boardDraw(ctx, 0, 0, -0.35, 110, 16, false, g); },
  pad(ctx, g) { gloveDraw(ctx, 0, 0, 0, 84, 16, false, g); },
};

// ---------- meta ----------
const meta: WorldMeta = {
  icon: '🍽️', strings, music: 'dishes.mp3',
  credits: { title: 'Ukulele in the Kitchen', artist: 'JoyInSound', id: 398306, url: 'https://pixabay.com/music/happy-childrens-tunes-ukulele-in-the-kitchen-398306/' },
  synth: { bpm: 76, root: 349.2, sc: 'mixolydian', prog: [0, 6, 3, 0], bar: 4, lead: 'pluck', dens: 0.45 },
};

const world = defineWorld({
  k: K,
  meta,
  scene,
  wash: 'rgba(240,250,255,.58)',
  bgExtra,
  skins,
  ball,
  goal,
  sounds,
  reskin,
  icons,
  parts,
  objs,
  levels,
});

export default world;
